import React, { useState, useEffect } from 'react';
import { 
  ClockIcon, 
  CheckCircleIcon, 
  XCircleIcon, 
  ExclamationTriangleIcon,
  ArrowUpIcon,
  ArrowDownIcon,
  CurrencyDollarIcon
} from '@heroicons/react/24/outline';
import btc1HistoryService from '../../services/btc1HistoryService';
import useAuthStore from '../../store/useAuthStore';
import { Link } from 'react-router-dom';

const TransactionHistory = ({ type = 'all', limit = 10, showTitle = true }) => {
  const { isAuthenticated } = useAuthStore();
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pagination, setPagination] = useState({
    total_count: 0,
    total_pages: 0,
    has_next: false,
    has_previous: false
  });

  useEffect(() => {
    if (isAuthenticated) {
      fetchHistory();
    } else {
      setLoading(false);
    }
  }, [type, limit, currentPage, isAuthenticated]);

  const fetchHistory = async () => {
    if (!isAuthenticated) {
      setLoading(false);
      return;
    }
    
    try {
      setLoading(true);
      setError('');
      
      const response = await btc1HistoryService.getTransactionHistory({ 
        type, 
        limit,
        page: currentPage
      });
      
      setTransactions(response.transactions || []);
      setPagination({
        total_count: response.total_count || 0,
        total_pages: response.total_pages || 0,
        has_next: response.has_next || false,
        has_previous: response.has_previous || false
      });
    } catch (err) {
      console.error('Error fetching transaction history:', err);
      setError('Failed to load transaction history');
    } finally {
      setLoading(false);
    }
  };

  const getStatusIcon = (status, type) => {
    switch (status) {
      case 'completed':
      case 'confirmed':
        return <CheckCircleIcon className="h-4 w-4 text-white" />;
      case 'pending':
        return <ClockIcon className="h-4 w-4 text-white" />;
      case 'failed':
        return <XCircleIcon className="h-4 w-4 text-red-400" />;
      default:
        return <ExclamationTriangleIcon className="h-4 w-4 text-white" />;
    }
  };

  const getTypeIcon = (txType, subtype) => {
    switch (txType) {
      case 'swap':
        return <ArrowUpIcon className="h-4 w-4 text-white" />;
      case 'deposit':
        return <ArrowDownIcon className="h-4 w-4 text-white" />;
      case 'transaction':
        return <CurrencyDollarIcon className="h-4 w-4 text-purple-400" />;
      default:
        return <CurrencyDollarIcon className="h-4 w-4 text-white" />;
    }
  };

  const formatAmount = (amount, currency) => {
    const num = parseFloat(amount);
    if (isNaN(num)) return '0';
    
    if (num >= 1000000) {
      return `${(num / 1000000).toFixed(2)}M ${currency}`;
    } else if (num >= 1000) {
      return `${(num / 1000).toFixed(2)}K ${currency}`;
    } else if (num < 0.01) {
      return `${num.toFixed(8)} ${currency}`;
    } else {
      return `${num.toFixed(6)} ${currency}`;
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    
    return date.toLocaleDateString();
  };

  const getTypeLabel = (txType, subtype) => {
    switch (txType) {
      case 'swap':
        return subtype === 'wallet_swap' ? 'Wallet Swap' : 'Crypto Swap';
      case 'deposit':
        return 'Crypto Deposit';
      case 'transaction':
        return 'BTC1 Transaction';
      case 'fiat_request':
        return 'Fiat Purchase Request';
      default:
        return 'Transaction';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed':
      case 'confirmed':
        return 'text-white';
      case 'pending':
        return 'text-white';
      case 'failed':
        return 'text-red-400';
      default:
        return 'text-white';
    }
  };

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
  };

  const handleNextPage = () => {
    if (pagination.has_next) {
      setCurrentPage(currentPage + 1);
    }
  };

  const handlePreviousPage = () => {
    if (pagination.has_previous) {
      setCurrentPage(currentPage - 1);
    }
  };

  const resetToFirstPage = () => {
    setCurrentPage(1);
  };

  // Show authentication prompt if user is not logged in
  if (!isAuthenticated) {
    return (
      <div className="bg-gray-800 rounded-lg p-4 sm:p-6">
        {showTitle && (
          <h3 className="text-base sm:text-lg font-medium text-white mb-3 sm:mb-4">Transaction History</h3>
        )}
        <div className="text-center py-6 sm:py-8">
          <CurrencyDollarIcon className="h-12 w-12 sm:h-16 sm:w-16 mx-auto mb-3 sm:mb-4 text-white" />
          <h4 className="text-base sm:text-lg font-medium text-white mb-2">Sign in to view your transactions</h4>
          <p className="text-sm sm:text-base text-white mb-4 sm:mb-6 px-2">Track your swap history and transaction details</p>
          <div className="flex flex-col sm:flex-row justify-center gap-3 sm:gap-4 sm:space-x-4">
            <Link 
              to="/login" 
              className="bg-[#0E1696] hover:bg-blue-700 !text-white px-6 py-2 rounded-lg text-sm sm:text-base font-medium transition-colors"
            >
              Sign In
            </Link>
            <Link 
              to="/register" 
              className="bg-gray-600 hover:bg-gray-700 text-white px-6 py-2 rounded-lg text-sm sm:text-base font-medium transition-colors"
            >
              Register
            </Link>
          </div>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="bg-gray-800 rounded-lg p-4">
        {showTitle && (
          <h3 className="text-base sm:text-lg font-medium text-white mb-3 sm:mb-4">Transaction History</h3>
        )}
        <div className="space-y-2 sm:space-y-3">
          {[...Array(3)].map((_, i) => (
            <div key={i} className="animate-pulse">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 sm:gap-0">
                <div className="flex items-center space-x-2 sm:space-x-3">
                  <div className="w-6 h-6 sm:w-8 sm:h-8 bg-gray-700 rounded-full"></div>
                  <div className="space-y-1.5 sm:space-y-2">
                    <div className="h-3 sm:h-4 bg-gray-700 rounded w-20 sm:w-24"></div>
                    <div className="h-2.5 sm:h-3 bg-gray-700 rounded w-14 sm:w-16"></div>
                  </div>
                </div>
                <div className="text-left sm:text-right space-y-1.5 sm:space-y-2">
                  <div className="h-3 sm:h-4 bg-gray-700 rounded w-16 sm:w-20"></div>
                  <div className="h-2.5 sm:h-3 bg-gray-700 rounded w-10 sm:w-12"></div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-gray-800 rounded-lg p-4">
        {showTitle && (
          <h3 className="text-base sm:text-lg font-medium text-white mb-3 sm:mb-4">Transaction History</h3>
        )}
        <div className="text-center text-red-400 py-4 sm:py-6">
          <p className="text-sm sm:text-base px-2 break-words">{error}</p>
          <button 
            onClick={fetchHistory}
            className="mt-3 sm:mt-4 text-xs sm:text-sm text-white hover:text-blue-300 transition-colors"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  if (transactions.length === 0) {
    return (
      <div className="bg-gray-800 rounded-lg p-4">
        {showTitle && (
          <h3 className="text-base sm:text-lg font-medium text-white mb-3 sm:mb-4">Transaction History</h3>
        )}
        <div className="text-center text-white py-6 sm:py-8">
          <CurrencyDollarIcon className="h-10 w-10 sm:h-12 sm:w-12 mx-auto mb-2 sm:mb-3 text-white" />
          <p className="text-sm sm:text-base">No transactions found</p>
          <p className="text-xs sm:text-sm mt-1">Your transaction history will appear here</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-gray-800 rounded-lg p-3 sm:p-4">
      {showTitle && (
        <div className="flex items-center justify-between mb-3 sm:mb-4 gap-2">
          <h3 className="text-base sm:text-lg font-medium text-white">Transaction History</h3>
          <button 
            onClick={resetToFirstPage}
            className="text-xs sm:text-sm text-white hover:text-blue-300 transition-colors whitespace-nowrap flex-shrink-0"
          >
            Refresh
          </button>
        </div>
      )}
      
      <div className="space-y-2 sm:space-y-3">
        {transactions.map((tx) => (
          <div key={tx.id} className="flex flex-col sm:flex-row sm:items-center sm:justify-between p-2.5 sm:p-3 bg-gray-700 rounded-lg hover:bg-gray-600 transition-colors gap-2 sm:gap-0">
            <div className="flex items-center space-x-2 sm:space-x-3 flex-1 min-w-0">
              <div className="flex items-center space-x-1.5 sm:space-x-2 flex-shrink-0">
                {getTypeIcon(tx.type, tx.subtype)}
                {getStatusIcon(tx.status, tx.type)}
              </div>
              
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-1.5 sm:gap-2">
                  <span className="text-white font-medium text-xs sm:text-sm truncate">
                    {getTypeLabel(tx.type, tx.subtype)}
                  </span>
                  <span className={`text-xs ${getStatusColor(tx.status)} flex-shrink-0`}>
                    {tx.status}
                  </span>
                </div>
                
                <div className="text-xs text-white mt-0.5">
                  {formatDate(tx.created)}
                </div>
              </div>
            </div>
            
            <div className="text-left sm:text-right flex-shrink-0 sm:ml-4">
              {tx.type === 'swap' && (
                <div className="text-white font-medium text-xs sm:text-sm break-words">
                  <span className="block sm:inline">{formatAmount(tx.from_amount, tx.from_currency)}</span>
                  <span className="hidden sm:inline"> → </span>
                  <span className="block sm:inline sm:ml-1">{formatAmount(tx.to_amount, tx.to_currency)}</span>
                </div>
              )}
              
              {tx.type === 'deposit' && (
                <div className="text-white font-medium text-xs sm:text-sm">
                  +{formatAmount(tx.amount, tx.currency)}
                </div>
              )}
              
              {tx.type === 'transaction' && (
                <div className="text-white font-medium text-xs sm:text-sm">
                  {parseFloat(tx.amount) > 0 ? '+' : ''}{formatAmount(tx.amount, tx.currency)}
                </div>
              )}
              
              {tx.usd_value && (
                <div className="text-xs text-white mt-0.5">
                  ${parseFloat(tx.usd_value).toFixed(2)}
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
      
      {/* Pagination Controls */}
      {pagination.total_pages > 1 && (
        <div className="mt-4 space-y-3 sm:space-y-0">
          {/* Pagination Info - Stack on mobile, inline on larger screens */}
          <div className="text-xs sm:text-sm text-white text-center sm:text-left">
            Showing {transactions.length} of {pagination.total_count} transactions
          </div>
          
          {/* Pagination Controls */}
          <div className="flex items-center justify-center sm:justify-end space-x-1 sm:space-x-2">
            {/* Previous Button */}
            <button
              onClick={handlePreviousPage}
              disabled={!pagination.has_previous}
              className="px-2 sm:px-3 py-1.5 sm:py-1 text-xs sm:text-sm bg-gray-700 text-white rounded disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-600 transition-colors min-w-[60px] sm:min-w-auto"
            >
              <span className="hidden sm:inline">Previous</span>
              <span className="sm:hidden">Prev</span>
            </button>
            
            {/* Page Numbers - Desktop (5 pages) */}
            <div className="hidden sm:flex items-center space-x-1">
              {Array.from({ length: Math.min(5, pagination.total_pages) }, (_, i) => {
                let pageNum;
                if (pagination.total_pages <= 5) {
                  pageNum = i + 1;
                } else if (currentPage <= 3) {
                  pageNum = i + 1;
                } else if (currentPage >= pagination.total_pages - 2) {
                  pageNum = pagination.total_pages - 4 + i;
                } else {
                  pageNum = currentPage - 2 + i;
                }
                
                return (
                  <button
                    key={pageNum}
                    onClick={() => handlePageChange(pageNum)}
                    className={`px-3 py-1 text-sm rounded min-w-[36px] transition-colors ${
                      currentPage === pageNum
                        ? 'bg-[#0E1696] text-white font-medium'
                        : 'bg-gray-700 text-white hover:bg-gray-600'
                    }`}
                  >
                    {pageNum}
                  </button>
                );
              })}
            </div>
            
            {/* Page Numbers - Mobile (3 pages) */}
            <div className="flex sm:hidden items-center space-x-0.5">
              {Array.from({ length: Math.min(3, pagination.total_pages) }, (_, i) => {
                let pageNum;
                if (pagination.total_pages <= 3) {
                  pageNum = i + 1;
                } else if (currentPage <= 2) {
                  pageNum = i + 1;
                } else if (currentPage >= pagination.total_pages - 1) {
                  pageNum = pagination.total_pages - 2 + i;
                } else {
                  pageNum = currentPage - 1 + i;
                }
                
                return (
                  <button
                    key={`mobile-${pageNum}`}
                    onClick={() => handlePageChange(pageNum)}
                    className={`px-2 py-1.5 text-xs rounded min-w-[32px] transition-colors ${
                      currentPage === pageNum
                        ? 'bg-[#0E1696] text-white font-medium'
                        : 'bg-gray-700 text-white hover:bg-gray-600'
                    }`}
                  >
                    {pageNum}
                  </button>
                );
              })}
            </div>
            
            {/* Next Button */}
            <button
              onClick={handleNextPage}
              disabled={!pagination.has_next}
              className="px-2 sm:px-3 py-1.5 sm:py-1 text-xs sm:text-sm bg-gray-700 text-white rounded disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-600 transition-colors min-w-[60px] sm:min-w-auto"
            >
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default TransactionHistory;
